import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { loadList } from '../../utils/loadState'
import { getExportTemplates, uploadExportTemplate, deleteExportTemplate } from '../../api'
import './TemplateLibrary.css'

export default function TemplateLibrary() {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [filterCategory, setFilterCategory] = useState('All')
  const [search, setSearch] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef()

  // A failed load is an error with a Retry, never an empty library (item 201).
  const [loadError, setLoadError] = useState(null)

  async function load() {
    setLoading(true)
    const { items, error } = await loadList(getExportTemplates, 'templates')
    setTemplates(items || [])
    setLoadError(error)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const categories = ['All', 'HCM', 'Payroll', 'Benefits', 'Time']

  const vendorColors = {
    UKG: 'var(--green)',
    ADP: '#d63a3a',
    Workday: '#2d5bc0',
    WD: '#2d5bc0',
    SAP: '#7c3aed',
    Oracle: '#ea580c',
  }

  // Mock templates if none loaded
  const displayTemplates = templates.length > 0 ? templates : [
    { id: '1', name: 'Employee Master Import', vendor: 'UKG', product: 'UKG Pro', category: 'HCM', column_count: 47, usage_count: 12, tags: ['Employee', 'Import', 'Core HR'] },
    { id: '2', name: 'Earning Code Setup', vendor: 'UKG', product: 'UKG Pro', category: 'Payroll', column_count: 23, usage_count: 8, tags: ['Payroll', 'Config'] },
    { id: '3', name: 'Deduction Code Setup', vendor: 'UKG', product: 'UKG Pro', category: 'Payroll', column_count: 31, usage_count: 8, tags: ['Payroll', 'Config'] },
    { id: '4', name: 'Workforce Now Export', vendor: 'ADP', product: 'ADP', category: 'Payroll', column_count: 38, usage_count: 5, tags: ['Payroll', 'Export'] },
    { id: '5', name: 'Worker EIB Template', vendor: 'Workday', product: 'Workday', category: 'HCM', column_count: 52, usage_count: 3, tags: ['Employee', 'Import'] },
    { id: '6', name: 'Benefits Enrollment', vendor: 'SAP', product: 'SAP SuccessFactors', category: 'Benefits', column_count: 28, usage_count: 2, tags: ['Benefits', 'Import'] },
  ]

  const filtered = displayTemplates.filter(t => {
    if (filterCategory !== 'All' && t.category !== filterCategory) return false
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await uploadExportTemplate(file, { name: file.name.replace(/\.[^.]+$/, ''), category: 'HCM' })
    } catch (err) {
      console.error('Upload failed:', err)
      alert('Upload failed: ' + err.message)
      return
    } finally {
      setUploading(false)
    }
    // Re-read through load(): a failed re-read shows as LoadError, never an empty library.
    load()
  }

  // Mock column mappings for selected template detail
  const mockMappings = [
    { source: 'employee_number', target: 'employee_id', match: 'auto' },
    { source: 'first_name', target: 'first_name', match: 'auto' },
    { source: 'last_name', target: 'last_name', match: 'auto' },
    { source: 'dept_code', target: 'department_id', match: 'manual' },
    { source: 'hire_dt', target: 'hire_date', match: 'auto' },
    { source: 'custom_field_3', target: '—', match: 'unmapped' },
    { source: 'pay_grp', target: 'pay_group', match: 'auto' },
  ]

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Template Library' },
  ]
  const navActions = (
    <>
      <button className="nav-btn" onClick={() => fileRef.current?.click()}>Upload Workbook</button>
      <button className="nav-btn primary">+ New Template</button>
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="tl-loading"><div className="tl-spinner" /><p>Loading template library…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleUpload} />

      <div className="tl-page">
        {/* Header */}
        <div className="tl-header">
          <div>
            <h1 className="tl-title">Template Library</h1>
            <p className="tl-subtitle">Vendor workbook templates for data conversion and migration</p>
          </div>
        </div>

        {/* Filters */}
        <div className="tl-filter-bar">
          <div className="tl-filter-pills">
            {categories.map(c => (
              <button key={c} className={`tl-filter-pill ${filterCategory === c ? 'active' : ''}`} onClick={() => setFilterCategory(c)}>{c}</button>
            ))}
          </div>
          <div className="tl-search-bar">
            <span className="tl-search-icon">⌕</span>
            <input className="tl-search-input" type="text" placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Upload Zone */}
        <div className="tl-upload-zone" onClick={() => fileRef.current?.click()}>
          <div className="tl-upload-icon">📋</div>
          <div className="tl-upload-text">Drop a multi-sheet vendor workbook here or <strong>click to browse</strong></div>
          <div className="tl-upload-hint">Supports .xlsx, .xls · Auto-detects sheets and creates individual templates</div>
          {uploading && <div className="tl-upload-progress">Uploading…</div>}
        </div>

        {loadError && <LoadError what="templates" error={loadError} onRetry={load} />}

        {/* Template Grid */}
        <div className="tl-grid">
          {filtered.map((t, i) => {
            const vendor = t.vendor || t.vendor_name || 'UKG'
            const vendorShort = vendor.length > 3 ? vendor.substring(0, 3).toUpperCase() : vendor
            return (
              <div key={t.id || i} className={`tl-card ${selectedTemplate?.id === t.id ? 'selected' : ''}`} onClick={() => setSelectedTemplate(t)}>
                <div className="tl-card-top">
                  <div className="tl-vendor-badge" style={{ background: vendorColors[vendor] || vendorColors[vendorShort] || 'var(--gray-500)' }}>
                    {vendorShort}
                  </div>
                  <div>
                    <div className="tl-card-name">{t.name}</div>
                    <div className="tl-card-vendor">{t.product || vendor} · {t.category || 'HCM'}</div>
                  </div>
                </div>
                <div className="tl-card-meta">
                  <span>{t.column_count || '—'} columns</span>
                  <span>·</span>
                  <span>Used {t.usage_count || 0} times</span>
                </div>
                {t.tags && (
                  <div className="tl-card-tags">
                    {t.tags.map(tag => <span key={tag} className="tl-tag">{tag}</span>)}
                  </div>
                )}
              </div>
            )
          })}
          {!loadError && filtered.length === 0 && (
            <div className="tl-empty">No templates match your filter.</div>
          )}
        </div>

        {/* Template Detail (inline) */}
        {selectedTemplate && (
          <div className="tl-detail-section">
            <div className="tl-subsection-title">Column Mapping — {selectedTemplate.name}</div>
            <div className="tl-mapping-meta">
              <span>{mockMappings.filter(m => m.match === 'auto').length} of {mockMappings.length} columns auto-mapped · {mockMappings.filter(m => m.match !== 'auto').length} need manual review</span>
              <button className="nav-btn" style={{ fontSize: 11 }}>✦ Auto-Map Remaining</button>
            </div>
            <div className="tl-mapping-table-wrap">
              <table className="tl-mapping-table">
                <thead>
                  <tr><th>Source Column</th><th></th><th>Target Column</th><th>Match</th></tr>
                </thead>
                <tbody>
                  {mockMappings.map((m, i) => (
                    <tr key={i}>
                      <td className="tl-mono">{m.source}</td>
                      <td className="tl-mapping-arrow">→</td>
                      <td className={`tl-mono ${m.match === 'unmapped' ? 'tl-unmapped' : ''}`}>{m.target}</td>
                      <td>
                        <span className={`tl-match-badge ${m.match}`}>
                          {m.match === 'auto' ? 'Auto' : m.match === 'manual' ? 'Manual' : 'Unmapped'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
